import * as THREE from 'three';
import { buildHumanoid, buildHeldItem, PLAYER_SKIN_DEFAULT, type HeldItemId } from './player';
import { NET, PVP } from '../config';
import type { RemotePose, RemoteAct } from '../net/connection';
import type { SleeperInfo } from '../net/protocol';

// Other players in the world (spec section 19, Phase M1). Poses arrive a few times a second over the
// relay. To make motion buttery we do NOT snap to each pose. Each remote keeps a short buffer of timed
// samples and is rendered at (now - interpDelay), interpolated between the two surrounding samples, so it
// glides. A speed-driven walk cycle sells the motion. Your own player is never in here (it is simulated
// locally every frame, so it is always perfectly smooth for you).

interface Sample { t: number; x: number; y: number; z: number; yaw: number; }
interface Remote {
  parts: ReturnType<typeof buildHumanoid>;
  buf: Sample[];
  walk: number;
  px: number;
  pz: number;
  held: string | null;   // wielded item shown in the hand
  crouch: boolean;
  crouchF: number;       // eased crouch amount 0..1
  swingT: number;        // seconds left of a swing animation
  fireT: number;         // seconds left of a muzzle flash
  flash: THREE.Mesh;     // muzzle flash quad at the hand, hidden until a shot
  name: string;          // display name shown on the floating tag
  tag: THREE.Sprite | null; // the name tag over the head (built lazily when the name arrives)
  torch: THREE.PointLight | null; // live warm light while this remote wields a torch (owner: remote torches must glow)
  // SLEEPER (disconnected player's body): when set, this remote is a body LYING ON ITS SIDE at a fixed
  // pose, not a live interpolated avatar. It is a valid damage + loot target. hp<=0 = a corpse.
  sleep: { x: number; y: number; z: number; yaw: number; hp: number } | null;
  down: boolean;         // live player is downed/dead (bleeding out): hold the prone pose, skip walk interpolation
  chops: number;         // local melee-hit count toward chopping this body apart (PVP.chopHits)
  faded?: boolean;       // MULTI-FLOOR: true while this remote is rendered as a faint ghost (a storey above you)
}

const MAX_EXTRAPOLATE_MS = 120; // if updates stall, extend motion this far past the last sample, then hold
const STOREY_ABOVE = 2.4;       // a remote whose feet are this far above yours is on a higher floor -> ghost it
const GHOST_OPACITY = 0.26;     // faint silhouette: you know someone's up there (fair scouting) but not crisply

// Ghost / un-ghost a remote's body when it is on a different floor. Leaves the muzzle flash + name-tag sprite
// alone (they own their own blend state); the body meshes are opaque Lambert, so toggling transparent/opacity/
// depthWrite is clean. This is what stops "I see her crisply THROUGH my ceiling" once the roof is cut away.
function fadeBody(r: Remote, opacity: number): void {
  const solid = opacity >= 1;
  r.parts.body.traverse((o) => {
    if (o === (r.flash as THREE.Object3D) || (o as THREE.Sprite).isSprite) return;
    const mat = (o as THREE.Mesh).material;
    if (!mat) return;
    for (const m of Array.isArray(mat) ? mat : [mat]) { m.transparent = !solid; m.opacity = opacity; m.depthWrite = solid; }
  });
}
const SWING_T = 0.42;           // swing animation length (matches the local player's feel)
const FIRE_T = 0.07;            // muzzle flash duration

export class RemotePlayerField {
  readonly group = new THREE.Group();
  private readonly players = new Map<string, Remote>();

  // Record a freshly received pose (creating the avatar on first sight).
  upsert(p: RemotePose, recvMs: number): void {
    let r = this.players.get(p.id);
    if (!r) {
      // ONE LIVE AVATAR PER NAME. A reconnect gives the same player a new socket id; if the server still
      // listed their old zombie socket (dead TCP lingers server-side for minutes), a joiner received BOTH and
      // rendered the player twice -- the clone survived relogs because the roster dump repeated it. Names are
      // unique per account, so an existing LIVE remote under the same name is always the stale one: drop it.
      if (p.name) {
        for (const [oid, or] of this.players) {
          if (oid !== p.id && !or.sleep && or.name === p.name) { this.remove(oid); break; }
        }
      }
      // Build this remote with ITS OWN skin (transmitted in the pose), NOT the local player's -- the fix for
      // "everyone looks the same colour". Missing skin (old server / a sleeper) falls back to the default look.
      const look = p.skin !== undefined && p.shirt !== undefined && p.pants !== undefined ? { skin: p.skin, shirt: p.shirt, pants: p.pants } : undefined;
      const parts = buildHumanoid(look);
      stripLightsAndShadows(parts.body); // a torch point light per remote would blow the light budget
      const flash = makeFlash();
      parts.handSocket.add(flash);
      this.group.add(parts.body);
      r = { parts, buf: [], walk: 0, px: p.x, pz: p.z, held: undefined as unknown as string | null, crouch: false, crouchF: 0, swingT: 0, fireT: 0, flash, name: '', tag: null, torch: null, sleep: null, down: false, chops: 0 };
      this.players.set(p.id, r);
    }
    // A live pose arriving for a body means the account woke (reconnected): clear the sleeper pose so the
    // avatar stands + interpolates again. (The server also sends 'wake', but this is a belt-and-braces path.)
    if (r.sleep) { r.sleep = null; r.chops = 0; this.wakePose(r); }
    // DOWNED live player (bleeding out / awaiting respawn): hold the prone pose (reuse the sleeper lie) at the
    // reported spot instead of the standing walk cycle. update() skips walk interpolation while `down` is set.
    if (p.down) {
      r.down = true;
      const b = r.parts.body;
      b.position.set(p.x, p.y, p.z);
      b.rotation.set(0, p.yaw, Math.PI / 2); // yaw, then tip onto the side, same as a sleeper body
      b.scale.y = 1;
      r.parts.leftLeg.rotation.set(0, 0, 0); r.parts.rightLeg.rotation.set(0.15, 0, 0);
      r.parts.rightArm.rotation.set(0.2, 0, 0); r.parts.leftArm.rotation.set(-0.1, 0, 0);
      r.flash.visible = false;
    } else if (r.down) {
      r.down = false;
      r.parts.body.rotation.set(0, 0, 0); r.parts.body.scale.y = 1; // stand back up (revived)
    }
    r.buf.push({ t: recvMs, x: p.x, y: p.y, z: p.z, yaw: p.yaw });
    const cutoff = recvMs - NET.bufferMs;
    while (r.buf.length > 2 && r.buf[0].t < cutoff) r.buf.shift();
    r.crouch = p.crouch;
    if (p.held !== r.held) { r.held = p.held; this.setHeld(r, p.held); } // swap the hand mesh when wielding changes
    if (p.name && p.name !== r.name) this.setName(r, p.name); // the tag rides every state msg; rebuild only on change
  }

  // Build (or replace) the floating name tag over a remote's head. A canvas-textured sprite: cheap,
  // camera-facing for free, and counter-scaled against the crouch squash so the label never squishes.
  private setName(r: Remote, name: string): void {
    r.name = name;
    // Dispose only the material + texture: Sprite geometry is SHARED across all sprites in three.
    if (r.tag) { r.parts.body.remove(r.tag); r.tag.material.map?.dispose(); r.tag.material.dispose(); }
    const canvas = document.createElement('canvas');
    canvas.width = 256; canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.font = '600 30px system-ui, sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.strokeStyle = 'rgba(10,14,18,0.9)'; ctx.lineWidth = 5;
    ctx.strokeText(name, 128, 32);
    ctx.fillStyle = '#e8eef4';
    ctx.fillText(name, 128, 32);
    const tex = new THREE.CanvasTexture(canvas);
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }));
    sprite.scale.set(1.5, 0.375, 1);
    sprite.position.set(0, 2.25, 0);
    r.parts.body.add(sprite);
    r.tag = sprite;
  }

  // Play a one-shot animation (swing / gunshot) on a remote.
  act(id: string, kind: RemoteAct): void {
    const r = this.players.get(id);
    if (!r) return;
    if (kind === 'swing') r.swingT = SWING_T;
    else r.fireT = FIRE_T;
  }

  private setHeld(r: Remote, held: string | null): void {
    const sock = r.parts.handSocket;
    for (let i = sock.children.length - 1; i >= 0; i--) { const c = sock.children[i]; if (c !== r.flash) { sock.remove(c); disposeTree(c); } }
    if (held) sock.add(buildHeldItem(held as Exclude<HeldItemId, null>));
    // Remote torch light (owner: other players' torches must actually glow). Add a warm PointLight at
    // the body's torch height while wielding a torch, remove it otherwise. A handful of live players in
    // M1 is well within the light budget; if the lobby grows, cap by distance in update().
    if (held === 'torch') {
      if (!r.torch) {
        r.torch = new THREE.PointLight(0xffb066, 34, 46, 1.1);
        r.torch.position.set(0.3, 1.5, 0.4);
        r.parts.body.add(r.torch);
      }
    } else if (r.torch) {
      r.parts.body.remove(r.torch);
      r.torch = null;
    }
  }

  // Mark a body as a SLEEPER (a disconnected player). Creates the avatar if we never saw them live (a body
  // already in the world at join). Lays it on its side at the fixed pose; the name tag stays upright. hp<=0
  // means the body is now a corpse (visually the same lying body -- both are loot/chop targets).
  sleeper(s: SleeperInfo): void {
    let r = this.players.get(s.id);
    if (!r) {
      // The body renders in ITS OWNER's colours (they ride the sleep broadcast + welcome roster). The fallback
      // is the fixed neutral default -- calling buildHumanoid() bare here defaulted to the VIEWER's own skin,
      // which is why a logged-off player's body wore whatever shirt the onlooker was wearing (audit E5).
      const look = { skin: s.skin ?? PLAYER_SKIN_DEFAULT.skin, shirt: s.shirt ?? PLAYER_SKIN_DEFAULT.shirt, pants: s.pants ?? PLAYER_SKIN_DEFAULT.pants };
      const parts = buildHumanoid(look);
      stripLightsAndShadows(parts.body);
      const flash = makeFlash();
      parts.handSocket.add(flash);
      this.group.add(parts.body);
      r = { parts, buf: [], walk: 0, px: s.x, pz: s.z, held: null, crouch: false, crouchF: 0, swingT: 0, fireT: 0, flash, name: '', tag: null, torch: null, sleep: null, down: false, chops: 0 };
      this.players.set(s.id, r);
    }
    const wasSleeping = !!r.sleep;
    r.sleep = { x: s.x, y: s.y, z: s.z, yaw: s.yaw, hp: s.hp };
    if (!wasSleeping) r.chops = 0; // a freshly downed body; a re-broadcast (hp change) keeps the local chop count
    if (s.name && s.name !== r.name) this.setName(r, s.name);
    this.layDown(r); // fixed lying pose (not interpolated)
  }

  // The account reconnected (or its body was chopped away): wake the avatar. If it is chopped-away we simply
  // remove it (no live socket will feed poses); if it reconnected, upsert() will re-add it on the next pose.
  wake(id: string): void {
    const r = this.players.get(id);
    if (!r) return;
    r.sleep = null; r.chops = 0;
    this.remove(id); // drop the lying body; a live reconnect re-creates a standing avatar via upsert()
  }

  // Drop EVERY remote (live + sleeping). Used at the start of a reconnect resync: any avatar whose leave/wake
  // we missed during the gap would otherwise linger as a shootable ghost forever (audit E4). The welcome's
  // sleeper roster + the post-hello state dump rebuild the real population immediately after.
  clearAll(): void {
    for (const id of [...this.players.keys()]) this.remove(id);
  }

  // Reset the rig from the lying pose back to standing (used if a pose arrives before the 'wake' message).
  private wakePose(r: Remote): void {
    r.parts.body.rotation.set(0, 0, 0);
    r.parts.body.position.y = r.buf.length ? r.parts.body.position.y : 0;
  }

  // Lay the humanoid on its side: tip the whole body around Z by ~90deg and drop it so the flank rests on
  // the ground. The name tag is re-parented upright by counter-rotating it (see update()).
  private layDown(r: Remote): void {
    const s = r.sleep!;
    const body = r.parts.body;
    body.position.set(s.x, s.y, s.z);
    body.rotation.set(0, s.yaw, Math.PI / 2); // yaw the body, then tip it onto its side (roll around Z)
    body.scale.y = 1;
    // Reset limbs to a neutral slump so it does not hold a mid-walk pose while down.
    r.parts.leftLeg.rotation.set(0, 0, 0);
    r.parts.rightLeg.rotation.set(0.15, 0, 0);
    r.parts.rightArm.rotation.set(0.2, 0, 0);
    r.parts.leftArm.rotation.set(-0.1, 0, 0);
    r.flash.visible = false;
  }

  // Is this remote a body (sleeper/corpse)? Exposed so the interact + hit paths only treat bodies as loot.
  isSleeper(id: string): boolean { return !!this.players.get(id)?.sleep; }

  // The remote's body root, for attaching things that ride a player (a thrown spear stuck in them).
  bodyOf(id: string): THREE.Object3D | null { return this.players.get(id)?.parts.body ?? null; }

  // Segment-vs-body-sphere test for bullets: mirrors AnimalField.hitTest so remotes + sleepers are shot the
  // same way. Returns the nearest hit body's id + ray param, or null. Live remotes AND sleepers are targets.
  hitTest(p0: THREE.Vector3, p1: THREE.Vector3): { id: string; t: number; head: boolean } | null {
    let bestId: string | null = null, bestT = 1, bestHead = false;
    for (const [id, r] of this.players) {
      const b = r.parts.body;
      const cx = b.position.x, cz = b.position.z;
      // HEAD first (smaller + higher): a round that clips the head sphere before the body counts as a headshot.
      const th = segSphereT(p0, p1, cx, b.position.y + PVP.headY, cz, PVP.headRadius);
      if (th >= 0 && th < bestT) { bestT = th; bestId = id; bestHead = true; }
      const t = segSphereT(p0, p1, cx, b.position.y + PVP.chestY, cz, PVP.bodyRadius);
      if (t >= 0 && t < bestT) { bestT = t; bestId = id; bestHead = false; }
    }
    return bestId ? { id: bestId, t: bestT, head: bestHead } : null;
  }

  // Bullet aim-assist spheres for every live remote + sleeper (mirrors AnimalField.aimCandidates).
  aimCandidates(px: number, pz: number, range: number): Array<{ pos: THREE.Vector3; radius: number }> {
    const out: Array<{ pos: THREE.Vector3; radius: number }> = [];
    const r2 = range * range;
    for (const r of this.players.values()) {
      const b = r.parts.body;
      const dx = b.position.x - px, dz = b.position.z - pz;
      if (dx * dx + dz * dz > r2) continue;
      out.push({ pos: new THREE.Vector3(b.position.x, b.position.y + PVP.chestY, b.position.z), radius: PVP.bodyRadius * 1.3 });
    }
    return out;
  }

  // Nearest LIVE (standing, non-sleeping) remote player to a point -- for the animal AI so herds flee from
  // WHOEVER is near, not only the host that runs the simulation. Sleeping/dead bodies don't spook animals.
  nearestLivePos(px: number, pz: number): { x: number; z: number } | null {
    let best: { x: number; z: number } | null = null, bestD = Infinity;
    for (const r of this.players.values()) {
      if (r.sleep) continue;
      const b = r.parts.body;
      const dx = b.position.x - px, dz = b.position.z - pz, dd = dx * dx + dz * dz;
      if (dd < bestD) { bestD = dd; best = { x: b.position.x, z: b.position.z }; }
    }
    return best;
  }

  // Visit each LIVE remote's ground position (skips sleepers + downed) -- used to stamp their grass trails so you
  // see where other players have walked, not just your own footprints.
  eachLivePos(cb: (x: number, z: number) => void): void {
    for (const r of this.players.values()) { if (r.sleep || r.down) continue; const b = r.parts.body; cb(b.position.x, b.position.z); }
  }

  // Nearest live remote OR sleeper within range of (px,pz) -- for melee aim. Returns the id + whether it is a body.
  nearest(px: number, pz: number, range: number): { id: string; body: boolean } | null {
    let best: { id: string; body: boolean } | null = null, bestD = range * range;
    for (const [id, r] of this.players) {
      const b = r.parts.body;
      const dx = b.position.x - px, dz = b.position.z - pz, dd = dx * dx + dz * dz;
      if (dd < bestD) { bestD = dd; best = { id, body: !!r.sleep }; }
    }
    return best;
  }

  // Nearest BODY (sleeper/corpse) within range -- for the loot-E prompt. Returns id + world pos, or null.
  nearestBody(px: number, pz: number, range: number): { id: string; x: number; z: number } | null {
    let best: { id: string; x: number; z: number } | null = null, bestD = range * range;
    for (const [id, r] of this.players) {
      if (!r.sleep) continue;
      const b = r.parts.body;
      const dx = b.position.x - px, dz = b.position.z - pz, dd = dx * dx + dz * dz;
      if (dd < bestD) { bestD = dd; best = { id, x: b.position.x, z: b.position.z }; }
    }
    return best;
  }

  // Register a local melee hit toward chopping a body. Returns true once PVP.chopHits is reached (the caller
  // then asks the authority to destroy it + spawn the satchel). Below the threshold it just jolts the body.
  registerChop(id: string): boolean {
    const r = this.players.get(id);
    if (!r || !r.sleep) return false;
    r.chops += 1;
    return r.chops >= PVP.chopHits;
  }

  remove(id: string): void {
    const r = this.players.get(id);
    if (!r) return;
    this.group.remove(r.parts.body);
    disposeTree(r.parts.body);
    this.players.delete(id);
  }

  count(): number { return this.players.size; }

  // Current rendered world position of a remote (for spatial audio), or null if unknown.
  posOf(id: string): { x: number; z: number } | null {
    const r = this.players.get(id);
    return r ? { x: r.parts.body.position.x, z: r.parts.body.position.z } : null;
  }

  // Visit every LIVE remote with its id + display name + position -- the map's teammate dots read this.
  eachLive(cb: (id: string, name: string, x: number, z: number) => void): void {
    for (const [id, r] of this.players) {
      if (r.sleep || r.down) continue;
      const b = r.parts.body;
      cb(id, r.name, b.position.x, b.position.z);
    }
  }

  // Render every remote at (now - interpDelay), interpolated, with an eased walk cycle by speed. `myFeetY` (the
  // local player's feet height) drives the multi-floor ghosting: a remote a storey above you fades to a faint
  // silhouette so a cut-away ceiling doesn't reveal them crisply. Defaults to Infinity = never ghost (safe no-op).
  update(nowMs: number, dt: number, myFeetY = Infinity): void {
    const target = nowMs - NET.interpDelayMs;
    const ease = Math.min(1, dt * 12);
    for (const r of this.players.values()) {
      // A sleeper (or a downed live player) holds its fixed lying pose; no interpolation. Its name tag is a
      // Sprite, so it stays camera-facing + upright for free regardless of the body's Z-tip -- skip it.
      if (r.sleep || r.down) continue;
      const s = sampleAt(r.buf, target);
      if (!s) continue;
      const body = r.parts.body;
      body.position.set(s.x, s.y, s.z);
      body.rotation.y = s.yaw;

      // MULTI-FLOOR ghosting: fade to a faint silhouette when a full storey above the local player (only re-touch
      // the materials on a transition, so it stays cheap).
      const above = s.y > myFeetY + STOREY_ABOVE;
      if ((r.faded ?? false) !== above) { fadeBody(r, above ? GHOST_OPACITY : 1); r.faded = above; }

      const speed = Math.hypot(s.x - r.px, s.z - r.pz) / Math.max(dt, 1e-3);
      r.px = s.x; r.pz = s.z;
      const moving = speed > 0.4;
      r.walk += moving ? dt * Math.min(speed, 6) * 2.2 : 0;
      const swing = Math.sin(r.walk) * 0.5;
      const tl = moving ? swing : 0, tr = moving ? -swing : 0;
      r.parts.leftLeg.rotation.x += (tl - r.parts.leftLeg.rotation.x) * ease;
      r.parts.rightLeg.rotation.x += (tr - r.parts.rightLeg.rotation.x) * ease;
      // Right arm: a swing arc overrides the walk sway for its duration (raise up+FORWARD, then slam
      // down through the FRONT). Mirrors the LOCAL player swing (player.ts): POSITIVE rotation.x = front
      // (the body faces -Z). The old negative arc swung BEHIND the body -- the "hit backwards" bug.
      if (r.swingT > 0) {
        r.swingT = Math.max(0, r.swingT - dt);
        const t = 1 - r.swingT / SWING_T;
        const windup = 0.55;
        if (t < windup) { const u = t / windup; r.parts.rightArm.rotation.x = 2.8 * (1 - (1 - u) * (1 - u)); }
        else { const u = (t - windup) / (1 - windup); r.parts.rightArm.rotation.x = 2.8 - 2.2 * (u * u); }
      } else {
        r.parts.rightArm.rotation.x += ((moving ? -swing * 0.6 : 0) - r.parts.rightArm.rotation.x) * ease;
      }
      // Muzzle flash + a little recoil while firing.
      if (r.fireT > 0) { r.fireT = Math.max(0, r.fireT - dt); r.flash.visible = true; r.parts.rightArm.rotation.x -= 0.35; }
      else r.flash.visible = false;
      // Crouch: compress the body toward the feet (a readable posture tell).
      r.crouchF += ((r.crouch ? 1 : 0) - r.crouchF) * ease;
      body.scale.y = 1 - 0.28 * r.crouchF;
      if (r.tag) r.tag.scale.y = 0.375 / body.scale.y; // counter the squash so the name tag never squishes
    }
  }
}

// Interpolated pose at time `t` from a time-ordered sample buffer.
function sampleAt(buf: Sample[], t: number): Sample | null {
  if (buf.length === 0) return null;
  if (buf.length === 1 || t <= buf[0].t) return buf[0];
  const last = buf[buf.length - 1];
  if (t >= last.t) {
    // past the newest sample: extrapolate briefly off the last two, then hold
    const prev = buf[buf.length - 2];
    const span = last.t - prev.t;
    if (span > 0 && t - last.t <= MAX_EXTRAPOLATE_MS) {
      const k = (t - last.t) / span;
      return {
        t,
        x: last.x + (last.x - prev.x) * k,
        y: last.y + (last.y - prev.y) * k,
        z: last.z + (last.z - prev.z) * k,
        yaw: last.yaw + shortestArc(prev.yaw, last.yaw) * k,
      };
    }
    return last;
  }
  for (let i = 0; i < buf.length - 1; i++) {
    const a = buf[i], b = buf[i + 1];
    if (t >= a.t && t <= b.t) {
      const k = (t - a.t) / Math.max(b.t - a.t, 1e-3);
      return {
        t,
        x: a.x + (b.x - a.x) * k,
        y: a.y + (b.y - a.y) * k,
        z: a.z + (b.z - a.z) * k,
        yaw: a.yaw + shortestArc(a.yaw, b.yaw) * k,
      };
    }
  }
  return last;
}

// Segment (p0->p1) vs sphere: the ray param t in [0,1] of the first entry, or -1 if it misses. Mirrors the
// bullet test in animals.ts so remotes + sleepers are shot exactly like a wild animal.
function segSphereT(p0: THREE.Vector3, p1: THREE.Vector3, cx: number, cy: number, cz: number, r: number): number {
  const dx = p1.x - p0.x, dy = p1.y - p0.y, dz = p1.z - p0.z;
  const fx = p0.x - cx, fy = p0.y - cy, fz = p0.z - cz;
  const aa = dx * dx + dy * dy + dz * dz; if (aa < 1e-9) return -1;
  const b = 2 * (fx * dx + fy * dy + fz * dz), c = fx * fx + fy * fy + fz * fz - r * r;
  const disc = b * b - 4 * aa * c; if (disc < 0) return -1;
  const t = (-b - Math.sqrt(disc)) / (2 * aa);
  return t >= 0 && t <= 1 ? t : -1;
}

// Smallest signed angle from `from` to `to`, so yaw lerps the short way around.
function shortestArc(from: number, to: number): number {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

// A small additive quad at the hand, shown for a few frames when a remote fires.
function makeFlash(): THREE.Mesh {
  const m = new THREE.Mesh(
    new THREE.PlaneGeometry(0.34, 0.34),
    new THREE.MeshBasicMaterial({ color: 0xffd27a, transparent: true, opacity: 0.95, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }),
  );
  m.position.set(0, 0.02, -0.78); // at the AK barrel tip (3rd-person AK lies along -Z; +Z was behind the shooter)
  m.visible = false;
  return m;
}

function stripLightsAndShadows(root: THREE.Object3D): void {
  const lights: THREE.Object3D[] = [];
  root.traverse((o) => {
    if ((o as THREE.PointLight).isPointLight) lights.push(o);
    const m = o as THREE.Mesh;
    if (m.isMesh) m.castShadow = false;
  });
  for (const l of lights) l.parent?.remove(l);
}

function disposeTree(obj: THREE.Object3D): void {
  obj.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
    const mat = m.material;
    if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
    else if (mat) {
      (mat as THREE.SpriteMaterial).map?.dispose(); // the name tag's canvas texture leaks without this
      (mat as THREE.Material).dispose();
    }
  });
}
